// Typed fetch wrapper. Same-origin; cookies sent automatically.
import type {
  AlcoholInput,
  BloodPanelSummary,
  BodyTrends,
  CaffeineInput,
  CoachNoteItem,
  CoachToday,
  Correlation,
  DayLogs,
  DaySummary,
  DeloadSignal,
  FavoriteItem,
  FoodCandidate,
  LogEntryInput,
  MarkerTrend,
  MealSuggestion,
  MoodInput,
  OnboardingInput,
  Profile,
  PushSubscriptionJSON,
  RoutineToday,
  RoutineUpdate,
  SessionInput,
  SessionWithSets,
  StreakInfo,
  SupplementStatus,
  TargetCalcResult,
  Targets,
  TodayTraining,
  WaterInput,
} from "@hh/shared";

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: "same-origin",
    headers: init?.body ? { "content-type": "application/json" } : undefined,
    ...init,
  });
  if (!res.ok) {
    let msg = `Request failed (${res.status})`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) msg = body.error;
    } catch { /* ignore */ }
    throw new ApiError(msg, res.status);
  }
  return (await res.json()) as T;
}

export class ApiError extends Error {
  constructor(message: string, public status: number) {
    super(message);
  }
}

export const api = {
  session: () => req<{ authed: boolean }>("/api/session"),
  login: (password: string) =>
    req<{ ok: true }>("/api/login", { method: "POST", body: JSON.stringify({ password }) }),
  logout: () => req<{ ok: true }>("/api/logout", { method: "POST" }),
  forgot: () => req<{ ok: true }>("/api/forgot", { method: "POST" }),
  reset: (code: string, password: string) =>
    req<{ ok: true }>("/api/reset", { method: "POST", body: JSON.stringify({ code, password }) }),

  profile: () =>
    req<{ profile: Profile | null; targets: Targets | null; onboarded: boolean }>("/api/profile"),
  onboardingPreview: (input: OnboardingInput) =>
    req<TargetCalcResult>("/api/onboarding/preview", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  onboarding: (input: OnboardingInput) =>
    req<{ profile: Profile; targets: TargetCalcResult }>("/api/onboarding", {
      method: "POST",
      body: JSON.stringify(input),
    }),

  // ---- Food ----
  day: (date?: string) =>
    req<DaySummary>(`/api/day${date ? `?date=${date}` : ""}`),
  favorites: () => req<{ favorites: FavoriteItem[] }>("/api/favorites"),
  barcode: (code: string) =>
    req<{ candidate: FoodCandidate }>(`/api/foods/barcode/${encodeURIComponent(code)}`),
  search: (q: string) =>
    req<{ candidates: FoodCandidate[]; usdaEnabled: boolean }>(
      `/api/foods/search?q=${encodeURIComponent(q)}`,
    ),
  parse: (text: string) =>
    req<{ candidates: FoodCandidate[] }>("/api/foods/parse", {
      method: "POST",
      body: JSON.stringify({ text }),
    }),
  photo: (image: string, media_type: string) =>
    req<{ candidates: FoodCandidate[] }>("/api/foods/photo", {
      method: "POST",
      body: JSON.stringify({ image, media_type }),
    }),
  logFood: (entries: LogEntryInput[]) =>
    req<{ ok: true; ids: number[] }>("/api/food-log", {
      method: "POST",
      body: JSON.stringify({ entries }),
    }),
  deleteFood: (id: number) =>
    req<{ ok: true }>(`/api/food-log/${id}`, { method: "DELETE" }),

  // ---- Training ----
  trainToday: () => req<TodayTraining>("/api/train/today"),
  trainProgram: () => req<{ week: { day: string; template: string; session: import("@hh/shared").PrescribedSession }[] }>("/api/train/program"),
  trainSessions: (limit = 15) => req<{ sessions: SessionWithSets[] }>(`/api/train/sessions?limit=${limit}`),
  logSession: (input: SessionInput) =>
    req<{ ok: true; id: number }>("/api/train/session", { method: "POST", body: JSON.stringify(input) }),
  deleteSession: (id: number) =>
    req<{ ok: true }>(`/api/train/session/${id}`, { method: "DELETE" }),
  deload: () => req<DeloadSignal>("/api/train/deload"),

  // ---- Body ----
  body: (days = 90) => req<BodyTrends>(`/api/body?days=${days}`),

  // ---- Routine ----
  routineToday: () => req<RoutineToday>("/api/routine/today"),
  routineUpdate: (u: RoutineUpdate) =>
    req<{ ok: true }>("/api/routine/today", { method: "POST", body: JSON.stringify(u) }),
  streak: () => req<StreakInfo>("/api/routine/streak"),

  // ---- Wellness ----
  logsToday: () => req<DayLogs>("/api/logs/today"),
  water: (b: WaterInput) => req<{ ok: true }>("/api/water", { method: "POST", body: JSON.stringify(b) }),
  waterUndo: () => req<{ ok: true }>("/api/water/undo", { method: "POST" }),
  caffeine: (b: CaffeineInput) => req<{ ok: true }>("/api/caffeine", { method: "POST", body: JSON.stringify(b) }),
  alcohol: (b: AlcoholInput) => req<{ ok: true }>("/api/alcohol", { method: "POST", body: JSON.stringify(b) }),
  mood: (b: MoodInput) => req<{ ok: true }>("/api/mood", { method: "POST", body: JSON.stringify(b) }),

  // ---- Correlations ----
  correlations: () => req<{ correlations: Correlation[] }>("/api/correlations"),

  // ---- Coach ----
  coachToday: () => req<CoachToday>("/api/coach/today"),
  suggestions: () => req<{ suggestions: MealSuggestion[]; proteinLeft: number; kcalLeft: number }>("/api/coach/suggestions"),
  coachNotes: () => req<{ notes: CoachNoteItem[] }>("/api/coach/notes?type=sunday"),
  generateReview: () => req<{ ok: true; content: string }>("/api/coach/review", { method: "POST" }),

  // ---- Supplements ----
  supplements: () => req<{ supplements: SupplementStatus[] }>("/api/supplements"),
  toggleSupplement: (id: number) =>
    req<{ ok: true; takenToday: boolean }>(`/api/supplements/${id}/log`, { method: "POST" }),

  // ---- Blood ----
  bloodPanels: () => req<{ panels: BloodPanelSummary[] }>("/api/blood/panels"),
  bloodTrends: () => req<{ trends: MarkerTrend[] }>("/api/blood/trends"),
  bloodUpload: (pdf_base64: string, filename: string, date?: string) =>
    req<{ ok: true; panel_date: string; markers: number }>("/api/blood/upload", {
      method: "POST", body: JSON.stringify({ pdf_base64, filename, date }),
    }),

  // ---- Push ----
  pushPublicKey: () => req<{ key: string | null }>("/api/push/public-key"),
  pushSubscribe: (sub: PushSubscriptionJSON) =>
    req<{ ok: true }>("/api/push/subscribe", { method: "POST", body: JSON.stringify(sub) }),
  pushTest: () => req<{ ok: true; sent: number }>("/api/push/test", { method: "POST" }),
};

// Live macros for a candidate at its current amount (mirrors the server math).
export function candidateMacros(c: FoodCandidate) {
  const base = c.unit === "serving" ? c.amount * (c.default_serving ?? 0) : c.amount;
  const f = base / 100;
  return {
    kcal: Math.round(c.per100_kcal * f),
    protein_g: Math.round(c.per100_protein * f),
    carbs_g: Math.round(c.per100_carbs * f),
    fat_g: Math.round(c.per100_fat * f),
  };
}
